import { PlayerRef } from '@remotion/player';
import { memo, useCallback, useContext, useMemo, useState, useSyncExternalStore } from 'react';
import { useRemotionPlayerPause } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPause';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import useRemotionPlayerDuration from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerDuration';
import { useRemotionPlayerCurrentTimeFromContext } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTimeFromContext';
import { RemotionPlayerContext } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/context/RemotionPlayerContext';
import ProgressBar from '@/Pages/HomePageNew/ClipRecommendation/components/ProgressBar';
import VideoControls from '@/Pages/HomePageNew/ClipRecommendation/components/VideoControls';
import VolumeControl from '@/Pages/HomePageNew/ClipRecommendation/components/VolumeControl';
import { SKIP_SECONDS } from '@/Pages/HomePageNew/ClipRecommendation/constants';
import { useAutoplayOnce } from '@/Pages/HomePageNew/ClipRecommendation/hooks/useAutoplayOnce';
import { usePlaybackCallbacks } from '@/Pages/HomePageNew/ClipRecommendation/hooks/usePlaybackCallbacks';
import { clamp } from '@/Pages/HomePageNew/ClipRecommendation/utils';
import PlaybackSpeedButton from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelinePlayerActionBar/PlaybackSpeedButton';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

type ClipRecommendationPlayerControlsProps = {
  autoPlay?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
};

function ClipRecommendationPlayerControls({
  autoPlay = false,
  onPlay,
  onPause
}: ClipRecommendationPlayerControlsProps) {
  const { playerRef } = useContext(RemotionPlayerContext);
  const { isPlaying, play, pause } = useRemotionPlayerPause();
  const { currentTime } = useRemotionPlayerCurrentTimeFromContext();
  const duration = useRemotionPlayerDuration();
  const { seekTo } = useRemotionPlayerSeek();

  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [previousVolume, setPreviousVolume] = useState(1);

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isPlaybackSpeedEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Playback_Speed];

  const canRender = useMemo(() => Number.isFinite(duration) && duration > 0, [duration]);

  useAutoplayOnce({ autoPlay, playerRef, play });
  usePlaybackCallbacks({ isPlaying, onPlay, onPause });

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, pause, play]);

  const seekToTime = useCallback(
    (time: number) => {
      const wasPlaying = isPlaying;
      seekTo(time);
      if (wasPlaying) {
        play();
      }
    },
    [isPlaying, play, seekTo]
  );

  const skipBackward = useCallback(() => {
    seekToTime(Math.max(0, currentTime - SKIP_SECONDS));
  }, [currentTime, seekToTime]);

  const skipForward = useCallback(() => {
    seekToTime(clamp(currentTime + SKIP_SECONDS, 0, duration));
  }, [currentTime, duration, seekToTime]);

  const applyVolume = useCallback(
    (nextVolume: number) => {
      const player = playerRef as PlayerRef | null;
      player?.setVolume?.(nextVolume);
    },
    [playerRef]
  );

  const toggleMute = useCallback(() => {
    if (isMuted) {
      setVolume(previousVolume);
      applyVolume(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      setVolume(0);
      applyVolume(0);
      setIsMuted(true);
    }
  }, [applyVolume, isMuted, previousVolume, volume]);

  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      const nextVolume = clamp(newVolume, 0, 1);
      setVolume(nextVolume);
      applyVolume(nextVolume);
      if (nextVolume === 0) {
        setIsMuted(true);
      } else {
        setIsMuted(false);
        setPreviousVolume(nextVolume);
      }
    },
    [applyVolume]
  );

  const handleSeek = useCallback(
    (time: number) => {
      seekToTime(time);
    },
    [seekToTime]
  );

  if (!canRender) {
    return null;
  }

  return (
    <div
      className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 flex h-[36%] w-full origin-bottom items-end bg-gradient-to-t from-black/70 via-black/10 to-transparent transition-all duration-300 ease-in-out ${
        isPlaying ? 'scale-y-0 opacity-0 group-hover:scale-y-100 group-hover:opacity-100' : 'scale-y-100 opacity-100'
      }`}
    >
      <div className="pointer-events-auto flex w-full flex-col gap-1.5 p-3">
        <ProgressBar currentTime={currentTime} duration={duration} onSeek={handleSeek} />

        <div className="flex w-full items-center justify-between pr-2">
          <VideoControls
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            onPlayPause={togglePlayPause}
            onSkipBackward={skipBackward}
            onSkipForward={skipForward}
          />

          <div className="flex items-center gap-2">
            {isPlaybackSpeedEnabled && <PlaybackSpeedButton />}
            <VolumeControl
              volume={volume}
              isMuted={isMuted}
              onVolumeChange={handleVolumeChange}
              onToggleMute={toggleMute}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(ClipRecommendationPlayerControls);
